import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { History, Plus, Notebook } from 'lucide-react';
import { fetchPagos, Pago } from '../../../services/pagosService';
import AddPagos from '../../../components/pagos/AddPagos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const PagosPage: React.FC = () => {
    const [pagos, setPagos] = useState<Pago[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState<'add' | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('pago', userLevelId); // Ajusta el modelo según tu backend

    const loadPagos = useCallback(async () => {
        if (!perm?.can_read) {
            setPagos([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchPagos();
            // Orden descendente por ID (más reciente primero)
            const sorted = [...data].sort((a, b) => b.id - a.id);
            setPagos(sorted);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadPagos();
        }
    }, [loadPagos, perm]);

    // Paginación
    const totalPages = Math.ceil(pagos.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPagos = pagos.slice(startIndex, startIndex + itemsPerPage);

    // Manejadores con actualización optimista
    const handlePagoCreated = (newPago: Pago) => {
        setPagos(prev => [newPago, ...prev]); // Nuevo al inicio
        // Opcional: reordenar si es necesario
        setModalState(null);
    };

    const formatNumber = (value: any): string => {
        if (value === null || value === undefined) return '-';
        const num = typeof value === 'number' ? value : parseFloat(value);
        return isNaN(num) ? '-' : num.toFixed(2);
    };

    if (perm === null) return <Loading text="Cargando pagos..." />;

    return <><LumaResourceWorkspace title="Pagos" description="Consulta y gestiona la información de tu coworking." group="Pagos" rows={pagos} columns={[{key:'codigo',label:'Código'},{key:'cita',label:'Reserva',render:r=>r.cita_info?.codigo||r.cita||'—'},{key:'tipo_pago_nombre',label:'Tipo de pago'},{key:'monto',label:'Monto',render:r=>lumaMoney(r.monto)},{key:'fecha_pago',label:'Fecha'}]} idKey="id" nameKey="codigo" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setModalState('add')}:undefined} onEdit={undefined} onDelete={undefined}></LumaResourceWorkspace><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                /><AddPagos
                    isOpen={modalState === 'add'}
                    onClose={() => setModalState(null)}
                    onCreated={handlePagoCreated}
                /></>;
};

export default PagosPage;


